import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { router } from './router';
import { queryClient } from './lib/query-client';
import { useUiStore, applyThemeToDom } from './stores/ui';
import { useAuthStore } from './stores/auth';
import { bindWsToAuth, wsClient } from './realtime/ws-client';
import './styles/globals.css';

// 启动期同步主题到 <html data-theme>
applyThemeToDom(useUiStore.getState().theme);
useUiStore.subscribe((s) => applyThemeToDom(s.theme));

// Dev 期是否启用 mock。默认禁用(联调走真实后端),设 VITE_USE_MOCK=true 才挂 mock 拦截。
if (import.meta.env.DEV && (import.meta.env.VITE_USE_MOCK ?? 'false') === 'true') {
  const [{ installDevMock }, { installDevWsMock }] = await Promise.all([
    import('./api/dev-mock'),
    import('./realtime/dev-ws-mock'),
  ]);
  installDevMock();
  installDevWsMock();
}

// 启动期连 WS(若已登录)+ 订阅 auth 变化自动 reconnect/disconnect
if (useAuthStore.getState().token) {
  wsClient.connect();
}
bindWsToAuth();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={250}>
        <RouterProvider router={router} />
        <Toaster />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
